import {Grid} from '@material-ui/core';
import {useHandleGWLBenefitTypeCdChange} from 'Quoting/components/hooks/useHandleGWLBenefitTypeCdChange';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {useMultipleValues} from 'Quoting/components/hooks/useMultipleValues';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {GPLU_COVERAGE_CODE_TO_COVERAGE_NAME_MAP, GPLU_WHOLE_LIFE_FIELD_ID, GPLUCoverageCode} from '../../../PlanTemplates/GWL/gpluConstants';
import {useModifyMultipleValuesQuoteFormErrors} from '../../../PlanTemplates/GWL/GWLUtilities';
import {
  getAmountValuePath,
  getGPLUBenefitScheduleBlockPropertyPaths,
  getGPLUUnderWritingBlockPropertyPaths,
} from '../PropertyPaths';

export const GPLUBenefitScheduleBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {modifyMVQuoteFormErrors} = useModifyMultipleValuesQuoteFormErrors();

  const {selectedPlan, selectedCoverageIndex} = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = getGPLUBenefitScheduleBlockPropertyPaths(selectedPlan?.planIndex, selectedCoverageIndex);

  const UWPropertyPaths = getGPLUUnderWritingBlockPropertyPaths(selectedPlan?.planIndex, selectedCoverageIndex);

  const {specifiedAmounts, handleSpecifiedAmounts} = useMultipleValues('GPLUBenefitScheduleMultipleValues');

  const handleBenefitTypeCdChange = useHandleGWLBenefitTypeCdChange(
    getQuoteFieldValue(UWPropertyPaths.guaranteedIssueAmount),
    GPLUCoverageCode.WHOLE_LIFE
  );

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'row-5e8a5fed-7788-4188-b68e-c684462de63f'},
      items: [
        {
          key: '68297af9-3f28-42e6-94f8-ca2884919889',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              label="Benefit Type"
              id={propertyPaths.benefitTypeCd}
              value={getQuoteFieldValue(propertyPaths.benefitTypeCd)}
              onChange={e => handleBenefitTypeCdChange(e.target.value as string, propertyPaths)}
              dropdownVariant="AttributeValue"
              searchKey="GPLUBenefitSchedule.benefitTypeCd"
              coverageCode={GPLUCoverageCode.WHOLE_LIFE}
              formFieldId={GPLU_WHOLE_LIFE_FIELD_ID.benefitTypeCd}
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'd7525833-3637-439a-bfe9-1af2ee113b74',
          hidden: getQuoteFieldValue(propertyPaths.benefitTypeCd) !== 'RangeValues',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Minimum Benefit Amount"
              id={propertyPaths.benefitMinAmount}
              value={getQuoteFieldValue(getAmountValuePath(propertyPaths.benefitMinAmount))}
              onChange={e => updateQuoteFieldValue(getAmountValuePath(propertyPaths.benefitMinAmount), e.target.value)}
              onKeyDown={e => {
                if (['.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              formFieldId={GPLU_WHOLE_LIFE_FIELD_ID.benefitMinAmount}
              type="number"
              adornment="money"
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: '74e9f486-94dc-43e1-aba0-262d15ad76c0',
          hidden: getQuoteFieldValue(propertyPaths.benefitTypeCd) !== 'MultipleValues',
          element: (
            <QuoteFormElement
              elementType="MultiSelect"
              label="Specified Benefit Amounts"
              id={propertyPaths.multipleValues}
              value={specifiedAmounts}
              onChange={val => {
                handleSpecifiedAmounts(val, GPLUCoverageCode.WHOLE_LIFE);
                const guaranteedIssueAmount = getQuoteFieldValue<number>(getAmountValuePath(UWPropertyPaths.guaranteedIssueAmount));
                modifyMVQuoteFormErrors(
                  val,
                  guaranteedIssueAmount,
                  `${GPLU_WHOLE_LIFE_FIELD_ID.specifiedBenefitAmount}__${selectedPlan?.planKey}`,
                  GPLU_COVERAGE_CODE_TO_COVERAGE_NAME_MAP[GPLUCoverageCode.WHOLE_LIFE]
                );
              }}
              dropdownVariant="AttributeValue"
              searchKey="GPLUBenefitScheduleMultipleValues.specifiedAmount"
              formFieldId={GPLU_WHOLE_LIFE_FIELD_ID.specifiedBenefitAmount}
              coverageCode={GPLUCoverageCode.WHOLE_LIFE}
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: '55d036d9-b6aa-4a1d-b612-ac8e0e6edbf3',
          hidden: getQuoteFieldValue(propertyPaths.benefitTypeCd) !== 'SingleValue',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Flat Benefit Amount"
              id={propertyPaths.flatBenefitAmount}
              value={getQuoteFieldValue(getAmountValuePath(propertyPaths.flatBenefitAmount))}
              defaultValue={getQuoteFieldValue(getAmountValuePath(UWPropertyPaths.guaranteedIssueAmount))}
              onChange={e => updateQuoteFieldValue(getAmountValuePath(propertyPaths.flatBenefitAmount), e.target.value)}
              onKeyDown={e => {
                if (['.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              formFieldId={GPLU_WHOLE_LIFE_FIELD_ID.flatBenefitAmount}
              type="number"
              adornment="money"
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {
        key: 'row-5cdc5f7f-9bec-4525-a6cc-619328a96325',
        hidden: getQuoteFieldValue(propertyPaths.benefitTypeCd) !== 'RangeValues',
      },
      items: [
        {
          key: 'b6165956-6d9f-4bb8-b26f-fc52ba418551',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maximum Benefit Amount"
              id={propertyPaths.benefitMaxAmount}
              value={getQuoteFieldValue(getAmountValuePath(propertyPaths.benefitMaxAmount))}
              onChange={e => updateQuoteFieldValue(getAmountValuePath(propertyPaths.benefitMaxAmount), e.target.value)}
              onKeyDown={e => {
                if (['.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              formFieldId={GPLU_WHOLE_LIFE_FIELD_ID.benefitMaxAmount}
              type="number"
              adornment="money"
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'bac581be-7124-40f1-9802-32b054a51940',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Increment"
              id={propertyPaths.incrementAmount}
              value={getQuoteFieldValue(getAmountValuePath(propertyPaths.incrementAmount))}
              onChange={e => updateQuoteFieldValue(getAmountValuePath(propertyPaths.incrementAmount), e.target.value)}
              onKeyDown={e => {
                if (['.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              formFieldId={GPLU_WHOLE_LIFE_FIELD_ID.incrementAmount}
              type="number"
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'row-f6859a2e-1d5e-40e9-a762-725e82fc2b74'},
      items: [
        {
          key: '9d12b6c4-fba9-47eb-8031-7c11f720a19d',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              label="Death Benefit-Option"
              id={propertyPaths.deathBenefitOptionCd}
              value={getQuoteFieldValue(propertyPaths.deathBenefitOptionCd)}
              onChange={e => updateQuoteFieldValue(propertyPaths.deathBenefitOptionCd, e.target.value)}
              dropdownVariant="AttributeValue"
              searchKey="GPLUBenefitScheduleBenefitOptions.deathBenefitOptionCd"
              coverageCode={GPLUCoverageCode.WHOLE_LIFE}
              required
              disabled
            />
          ),
        },
      ],
    },
  ];

  return (
    <FormWrapper title="Benefit Schedule" collapsible withSeparator expanded>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows} />
      </Grid>
    </FormWrapper>
  );
};
